/* One parser serves both exact-size choices and area ordering. */
(function (root) {
  'use strict';

  function parseDimension(raw) {
    const match = String(raw).trim().match(/^H:(\d+(?:\.\d*)?|\.\d+)\*W:(\d+(?:\.\d*)?|\.\d+) Inches$/);
    if (!match) return null;
    const height = Number(match[1]);
    const width = Number(match[2]);
    const area = height * width;
    if (!(height > 0 && width > 0 && Number.isFinite(area))) return null;
    return { height, width, area, key: `${height}x${width}`, label: `H: ${match[1]} × W: ${match[2]} in` };
  }

  function dimensions(values) {
    return [...new Map(values.map(parseDimension).filter(Boolean).map(size => [size.key, size])).values()];
  }

  function compareText(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
  }

  function compareSize(a, b) {
    return a.area - b.area || a.height - b.height || a.width - b.width;
  }

  function smallest(card) {
    return card.sizes.slice().sort(compareSize)[0];
  }

  function compareCards(a, b, sort) {
    const first = smallest(a);
    const second = smallest(b);
    const direction = sort.endsWith('-desc') ? -1 : 1;
    let order = 0;
    if (sort.startsWith('price-')) {
      order = (a.price - b.price) * direction;
    } else {
      if (!first !== !second) return first ? -1 : 1;
      if (first && second) order = (first.area - second.area) * direction;
    }
    // Keep equal dimensions together in both directions before comparing titles.
    return order || (first && second ? compareSize(first, second) : first ? -1 : second ? 1 : 0)
      || compareText(a.title.toLowerCase(), b.title.toLowerCase()) || compareText(a.id, b.id);
  }

  function selectCards(cards, size, sort) {
    return cards.filter(card => !size || card.sizes.some(value => value.key === size))
      .slice().sort((a, b) => compareCards(a, b, sort));
  }

  function isComplete(total, cards) {
    return Number.isInteger(total) && total >= 0 && total <= 250 && total === cards.length;
  }

  const api = { parseDimension, dimensions, compareSize, selectCards, isComplete };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document) return;

  const controllers = new WeakMap();

  function enhance(container) {
    if (controllers.has(container)) return controllers.get(container);
    const host = container.closest('collection-component');
    if (!host) return;
    const state = { size: '', sort: 'area-asc' };
    const observer = new MutationObserver(refresh);

    function refresh() {
      observer.disconnect();
      try {
        const toolbar = container.querySelector('.myraasa-catalog');
        if (!toolbar) return;
        const controls = toolbar.querySelector('.myraasa-catalog__controls');
        const notice = toolbar.querySelector('[data-catalog-unavailable]');
        const nodes = Array.from(container.querySelectorAll('[data-catalog-card]'));
        let metadataValid = true;
        const cards = nodes.map(node => {
          let values;
          try {
            values = JSON.parse(node.dataset.catalogDimensions || '[]');
          } catch (error) {
            console.error('Unable to read painting dimensions for catalog sorting.', node.dataset.catalogId, error);
            metadataValid = false;
            values = [];
          }
          if (!Array.isArray(values)) {
            console.error('Painting dimensions must be a list for catalog sorting.', node.dataset.catalogId);
            metadataValid = false;
          }
          return {
            node, id: node.dataset.catalogId, title: node.dataset.catalogTitle,
            price: Number(node.dataset.catalogPrice), sizes: dimensions(Array.isArray(values) ? values : []),
          };
        });
        const complete = metadataValid && isComplete(Number(toolbar.dataset.catalogTotal), cards);
        host.classList.toggle('myraasa-catalog-active', complete);
        controls.hidden = !complete;
        notice.hidden = complete;
        if (!complete) {
          notice.textContent = metadataValid
            ? 'Size controls are unavailable because this collection cannot be shown in full. Use the native filters, sorting and pagination below.'
            : 'Painting size data could not be read. Use the native filters and sorting below.';
          nodes.forEach(node => { node.hidden = false; });
          return;
        }

        const sizeSelect = controls.querySelector('[data-catalog-size]');
        const sortSelect = controls.querySelector('[data-catalog-sort]');
        const groups = new Map();
        cards.forEach(card => card.sizes.forEach(size => {
          const group = groups.get(size.key) || { ...size, count: 0 };
          group.count++;
          groups.set(size.key, group);
        }));
        sizeSelect.replaceChildren(new Option('All sizes', ''));
        [...groups.values()].sort(compareSize).forEach(size => {
          sizeSelect.add(new Option(`${size.label} (${size.count})`, size.key));
        });
        // Preserve an active size even when a native filter temporarily removes its group.
        if (state.size && !groups.has(state.size)) {
          sizeSelect.add(new Option(`${state.size.replace('x', ' × ')} in (0)`, state.size));
        }
        sizeSelect.value = state.size;
        sortSelect.value = state.sort;
        const selected = selectCards(cards, state.size, state.sort);
        const visible = new Set(selected);
        cards.forEach(card => { card.node.hidden = !visible.has(card); });
        const grid = container.querySelector('ul#product-grid');
        if (grid) {
          [...selected, ...cards.filter(card => !visible.has(card))].forEach(card => grid.appendChild(card.node));
        }
        controls.querySelector('[data-catalog-count]').textContent =
          `${selected.length} of ${cards.length} paintings`;
        controls.querySelector('[data-catalog-empty]').hidden = selected.length !== 0;
      } finally {
        observer.observe(container, { childList: true, subtree: true });
      }
    }

    container.addEventListener('change', event => {
      if (event.target.matches('[data-catalog-size]')) state.size = event.target.value;
      else if (event.target.matches('[data-catalog-sort]')) state.sort = event.target.value;
      else return;
      refresh();
    });
    container.addEventListener('click', event => {
      if (!event.target.closest('[data-catalog-reset]')) return;
      state.size = '';
      state.sort = 'area-asc';
      refresh();
    });
    controllers.set(container, { refresh, state });
    refresh();
    return controllers.get(container);
  }

  function start() {
    root.document.querySelectorAll('#ProductGridContainer').forEach(enhance);
  }
  start();
  root.document.addEventListener('shopify:section:load', start);
})(typeof window === 'undefined' ? globalThis : window);
